import React, { useEffect, useMemo, useState, useRef } from "react";
import { db } from "../firebase";
import VideoPlayer from "../components/VideoPlayer";
import {
  doc,
  onSnapshot,
  getDocs,
  query,
  where,
  collection,
  getDoc,
  setDoc,
  updateDoc,
  arrayUnion,
  serverTimestamp,
} from "firebase/firestore";
import { ACCOUNT_TYPES, useAccountType } from "../hooks/useAccountType";
import { useAuth } from "../hooks/useAuth";
import { checkFourByFourStatus, checkAssociateStatus } from "../services/getAlldata";
import { trackerDataService } from "../services/trackerDataService";

/* ------------------------ CONFIG ------------------------ */

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const ROAD_SCENE_HEIGHT = 350;
const VIDEO_DOCK_HEIGHT = 80;
const CHECKBOX_SECTION_HEIGHT = 120;

const MODULES = [
  { id: "m0", title: "Intro", field: "intro_local", section: "start" },
  {
    id: "m1",
    title: "Multi Handed",
    field: "associate.multiHanded",
    section: "philosophy",
  },
  {
    id: "m2",
    title: "10% / 3 Rules / 3 Goals",
    field: "associate.tenThreResultsGoals",
    section: "philosophy",
  },
  {
    id: "m3",
    title: "Self Improvement",
    field: "associate.selfImprovement",
    section: "philosophy",
  },
  {
    id: "m4",
    title: "Observe 4 Recruits",
    field: "associate.milestones.observe4Recruits",
    section: "followSystem",
  },
  {
    id: "m5",
    title: "Observe 4 Clients",
    field: "associate.milestones.observe4Clients",
    section: "followSystem",
  },
  {
    id: "m6",
    title: "Get License",
    field: "getLicensed",
    section: "followSystem",
  },
  {
    id: "m7",
    title: "1 Direct Registration",
    field: "associate.registrationConvention",
    section: "followSystem",
  },
  {
    id: "m8",
    title: "9 Recruits",
    field: "associate.recruitsTtl",
    section: "buildOutlet",
  },
  {
    id: "m9",
    title: "45,000 Personal Points",
    field: "associate.personalPoints",
    section: "buildOutlet",
  },
  {
    id: "m10",
    title: "3 Licenses",
    field: "associate.licensesInTtl",
    section: "buildOutlet",
  },
  {
    id: "m11",
    title: "15 Registrations on Base",
    field: "associate.registrationsBase",
    section: "buildOutlet",
  },
];

const MODULE_POSITIONS = {
  m0: 0,
  m1: 2,
  m2: 3,
  m3: 4,
  m4: 6,
  m5: 7,
  m6: 8,
  m7: 9,
  m8: 11,
  m9: 12,
  m10: 13,
  m11: 14,
};

const TOTAL_POSITIONS = 16;

const MODULE_VIDEOS_FREE = {
  m0: [
    {
      title: "Intro",
      url: "https://player.vimeo.com/video/1155086400?h=a96efe5109",
      description: "",
    },
  ],
  m1: [
    {
      title: "Multi Handed",
      url: "https://player.vimeo.com/video/1154890841?h=843c23fbda",
      description: "",
    },
  ],
  m2: [
    {
      title: "10%, 3 Rules, 3 Goals",
      url: "https://player.vimeo.com/video/1155146645?h=60577b9f8d",
      description: "",
    },
  ],
  m3: [
    {
      title: "Self Improvement",
      url: "https://player.vimeo.com/video/1155094518?h=dc042bc51b",
      description: "",
    },
  ],
  m4: [
    {
      title: "System",
      url: "https://player.vimeo.com/video/1055385293?h=e177338da3",
      description: "",
    },
  ],
};

const MODULE_VIDEOS_PAID = {
  m0: [
    {
      title: "Intro",
      url: "https://player.vimeo.com/video/1155155836?h=212a8aedb5",
      description: "",
    },
  ],
  m1: [
    {
      title: "How to Create unlimited Market",
      url: "https://player.vimeo.com/video/1155091681?h=40bb712b5a",
      description: "",
    },
    {
      title: "How to approach",
      url: "https://player.vimeo.com/video/1154912714?h=2c604585ac",
      description: "",
    },
    {
      title: "How to Edify",
      url: "https://player.vimeo.com/video/1154891046?h=87efd37d2e",
      description: "",
    },
  ],
  m2: [
    {
      title: "10%, 3 Rules, 3 Goals",
      url: "https://player.vimeo.com/video/1155146645?h=60577b9f8d",
      description: "",
    },
  ],
  m3: [
    {
      title: "Self-Improvement",
      url: "https://player.vimeo.com/video/1155094518?h=dc042bc51b",
      description: "",
    },
  ],
  m4: [
    {
      title: "System",
      url: "https://player.vimeo.com/video/1055385293?h=e177338da3",
      description: "",
    },
  ],
};

const SECTIONS = [
  {
    name: "",
    startIndex: 0,
    endIndex: 0,
    badge: null,
  },
  {
    name: "BELIEVE PHILOSOPHY",
    startIndex: 2,
    endIndex: 6,
    nameClass: "section-title-philosophy",
    badge: {
      image: "./shield.png",
      type: "shield",
      className: "section-badge-philosophy",
    },
  },
  {
    name: "FOLLOW SYSTEM",
    startIndex: 6,
    endIndex: 11,
    badge: { image: "./shirt.png", type: "shirt" },
  },
  {
    name: "BUILD OUTLET",
    startIndex: 13,
    endIndex: 16,
    badge: { image: "./watch.png", type: "watch" },
  },
];

const onboardingProgressService = {
  async getProgress(userId) {
    try {
      const docRef = doc(db, 'trackers', userId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        return data.onboarding || this.getDefaultProgress();
      } else {
        const initialProgress = this.getDefaultProgress();
        await setDoc(docRef, {
          id: userId,
          onboarding: initialProgress,
          lastActivity: serverTimestamp(),
          createdAt: serverTimestamp()
        });
        return initialProgress;
      }
    } catch (error) {
      console.error('Error getting progress:', error);
      throw error;
    }
  },

  async completeModule(userId, moduleId) {
    try {
      const docRef = doc(db, 'trackers', userId);
      await updateDoc(docRef, {
        'onboarding.completedModules': arrayUnion(moduleId),
        'onboarding.lastActivity': serverTimestamp(),
        lastActivity: serverTimestamp()
      });
      
      await this.updateTotalProgress(userId);
    } catch (error) {
      console.error('Error completing module:', error);
      throw error;
    }
  },

  async markVideoWatched(userId, moduleId, videoIndex) {
    try {
      const progressKey = `${moduleId}_video_${videoIndex}`;
      const docRef = doc(db, 'trackers', userId);
      await updateDoc(docRef, {
        [`onboarding.videoProgress.${progressKey}`]: true,
        'onboarding.lastActivity': serverTimestamp(),
        lastActivity: serverTimestamp()
      });
    } catch (error) {
      console.error('Error marking video watched:', error);
      throw error;
    }
  },

  async unlockSection(userId, sectionName) {
    try {
      const docRef = doc(db, 'trackers', userId);
      await updateDoc(docRef, {
        'onboarding.unlockedSections': arrayUnion(sectionName),
        'onboarding.lastActivity': serverTimestamp(),
        lastActivity: serverTimestamp()
      });
    } catch (error) {
      console.error('Error unlocking section:', error);
      throw error;
    }
  },

  async updateTotalProgress(userId) {
    try {
      const progress = await this.getProgress(userId);
      const totalModules = 12;
      const completedCount = progress.completedModules.length;
      const totalProgress = Math.round((completedCount / totalModules) * 100);
      
      const docRef = doc(db, 'trackers', userId);
      await updateDoc(docRef, {
        'onboarding.totalProgress': totalProgress,
        'onboarding.lastActivity': serverTimestamp(),
        lastActivity: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating progress:', error);
      throw error;
    }
  },

  async resetProgress(userId) {
    try {
      const docRef = doc(db, 'trackers', userId);
      const defaultProgress = this.getDefaultProgress();
      
      await updateDoc(docRef, {
        'onboarding': defaultProgress,
        lastActivity: serverTimestamp()
      });
    } catch (error) {
      console.error('Error resetting progress:', error);
      throw error;
    }
  },

  async markCheckboxComplete(userId, moduleId, checkboxIndex) {
    try {
      const progressKey = `${moduleId}_checkbox_${checkboxIndex}`;
      const docRef = doc(db, 'trackers', userId);
      await updateDoc(docRef, {
        [`onboarding.checkboxProgress.${progressKey}`]: true,
        'onboarding.lastActivity': serverTimestamp(),
        lastActivity: serverTimestamp()
      });
    } catch (error) {
      console.error('Error marking checkbox complete:', error);
      throw error;
    }
  },

  getDefaultProgress() {
    return {
      completedModules: [],
      videoProgress: {},
      checkboxProgress: {},
      unlockedSections: ['start'],
      totalProgress: 0,
      lastActivity: serverTimestamp()
    };
  }
};

const ORDER = MODULES.map((m) => m.id);

function isTruthy(v) {
  if (v === true) return true;
  if (typeof v === "string")
    return ["true", "yes", "y", "1", "checked", "done"].includes(
      v.toLowerCase()
    );
  if (typeof v === "number") return v !== 0;
  return false;
}

function toNumber(v) {
  if (typeof v === "number") return v;
  if (typeof v === "string") {
    const n = Number(v.replace(/[, ]/g, ""));
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
}

function getDeepValue(obj, path) {
  return path.split(".").reduce((acc, key) => acc && acc[key], obj);
}

function computeModuleCompletion(moduleId, data) {
  const module = MODULES.find((m) => m.id === moduleId);
  if (!module) return false;

  const field = module.field;
  const value = getDeepValue(data, field);

  if (moduleId === "m8") return toNumber(value) >= 9;
  if (moduleId === "m9") return toNumber(value) >= 45000;
  if (moduleId === "m10") return toNumber(value) >= 3;
  if (moduleId === "m11") return toNumber(value) >= 15;

  return isTruthy(value);
}

/* ------------------------ MAIN COMPONENT ------------------------ */

export default function OnboardingRoad() {
  const { plan, isPaid, isFree, loading: accountLoading } = useAccountType();
  const { user } = useAuth();
  
  const [firebaseVideos, setFirebaseVideos] = useState(null);
  const [videosLoading, setVideosLoading] = useState(true);
  const [firebaseProgress, setFirebaseProgress] = useState({
    completedModules: [],
    videoProgress: {},
    checkboxProgress: {},
    unlockedSections: ['start'],
    totalProgress: 0
  });
  const [progressLoading, setProgressLoading] = useState(false);
  const [viewMode, setViewMode] = useState("trainee");
  const [recruiterEmail, setRecruiterEmail] = useState();
  const [data, setData] = useState({});
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState("");
  const [activeModule, setActiveModule] = useState(null);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [videoWatched, setVideoWatched] = useState(false);
  const [trackerModalOpen, setTrackerModalOpen] = useState(null);
  const [prospectTableModal, setProspectTableModal] = useState({
    isOpen: false,
    filterType: null,
    userId: null,
    userName: null,
    modalSource: null
  });
  const [allUsers, setAllUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [selectedUserName, setSelectedUserName] = useState("");
  const [userSearchTerm, setUserSearchTerm] = useState("");
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [showCheckboxes, setShowCheckboxes] = useState(false);


  const currentUserId = selectedUserId || user?.uid;
  const currentUserName = selectedUserName || user?.displayName || user?.email;

  // ... existing states ...
  const [trackerData, setTrackerData] = useState({
    recruitsTtl: 0,
    personalPoints: 0,
    licensesTtl: 0,
    registrationsBase: 0
  });


   useEffect(() => {
    const fetchTrackerData = async () => {
      if (!currentUserId) {
        setTrackerData({
          recruitsTtl: 0,
          personalPoints: 0,
          licensesTtl: 0,
          registrationsBase: 0
        });
        return;
      }

      try {
        const data = await trackerDataService.getTrackerData(currentUserId);
        setTrackerData({
          recruitsTtl: data.recruitsTtl,
          personalPoints: data.personalPoints,
          licensesTtl: data.licensesTtl,
          registrationsBase: data.registrationsBase
        });
      } catch (error) {
        console.error("Error loading tracker data:", error);
        setTrackerData({
          recruitsTtl: 0,
          personalPoints: 0,
          licensesTtl: 0,
          registrationsBase: 0
        });
      } finally {
      }
    };

    fetchTrackerData();
  }, [currentUserId]);


  const calculateCellWidth = () => {
    // Size columns based on viewport; keep them within a comfortable desktop range
    const availableWidth = Math.max(window.innerWidth - 120, 960);
    return clamp(Math.floor(availableWidth / TOTAL_POSITIONS), 56, 90);
  };

  const [cellWidth, setCellWidth] = useState(calculateCellWidth());
  const leftMargin = useMemo(() => clamp(Math.round(cellWidth * 0.9), 40, 90), [cellWidth]);


  const BLOCKS = [
    ["m0"],
    ["m1", "m2", "m3"],
    ["m4", "m5", "m6", "m7"],
    ["m8", "m9", "m10", "m11"],
  ];
  const CAR_ANCHOR_INDEX = [0, 2, 5, 10];

  const [carIndex, setCarIndex] = useState(0);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const usersSnapshot = await getDocs(collection(db, "trackers"));
        const usersList = usersSnapshot.docs.map(doc => ({
          id: doc.id,
          email: doc.data().email,
          name: doc.data().name || doc.data().email,
          status: doc.data().status
        })).filter(u => u.status === "Active");
        setAllUsers(usersList);
      } catch (err) {
        console.error("Error fetching users:", err);
      }
    };
    fetchUsers();
  }, []);

  useEffect(() => {
    const loadVideosFromFirebase = async () => {
      try {
        const docRef = doc(db, "config", "onboarding_videos");
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          const data = docSnap.data();
          const convertedFree = {};
          const convertedPaid = {};
          
          Object.keys(data.free || {}).forEach(moduleId => {
            const video = data.free[moduleId];
            if (video && video.url) {
              let embedUrl = video.url;
              if (embedUrl.includes("vimeo.com") && !embedUrl.includes("player.vimeo.com")) {
                try {
                  const u = new URL(embedUrl);
                  const parts = u.pathname.split("/").filter(Boolean);
                  const id = parts[0] === "video" ? parts[1] : parts[0];
                  const h = u.searchParams.get("h") || (parts[1] || "");
                  if (id) {
                    embedUrl = `https://player.vimeo.com/video/${id}` + (h ? `?h=${h}` : "");
                  }
                } catch {}
              }
              convertedFree[moduleId] = [{
                title: video.title || `Module ${moduleId}`,
                url: embedUrl,
                description: video.description || ""
              }];
            }
          });
          
          Object.keys(data.paid || {}).forEach(moduleId => {
            const video = data.paid[moduleId];
            if (video && video.url) {
              convertedPaid[moduleId] = [{
                title: video.title || `Module ${moduleId}`,
                url: video.url,
                description: video.description || ""
              }];
            }
          });
          
          setFirebaseVideos({
            free: convertedFree,
            paid: convertedPaid
          });
        }
      } catch (error) {
        console.error("Error loading videos from Firebase:", error);
      } finally {
        setVideosLoading(false);
      }
    };
    
    loadVideosFromFirebase();
  }, []);
  
  useEffect(() => {
    const loadProgress = async () => {
      if (!currentUserId) return;
      
      try {
        setProgressLoading(true);
        const progress = await onboardingProgressService.getProgress(currentUserId);
        setFirebaseProgress(progress);
      } catch (error) {
        console.error('Failed to load progress:', error);
        setFirebaseProgress(onboardingProgressService.getDefaultProgress());
      } finally {
        setProgressLoading(false);
      }
    };

    loadProgress();
  }, [currentUserId]);

  useEffect(() => {
    if (!currentUserId) return;

    const progressRef = doc(db, 'trackers', currentUserId);
    const unsubscribe = onSnapshot(progressRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        setFirebaseProgress(data.onboarding || onboardingProgressService.getDefaultProgress());
        setData(data);
      }
    });

    return () => unsubscribe();
  }, [currentUserId]);

  const filteredUsers = useMemo(() => {
    if (!userSearchTerm) return allUsers;
    const term = userSearchTerm.toLowerCase();
    return allUsers.filter(user => 
      user.name.toLowerCase().includes(term) || 
      user.email.toLowerCase().includes(term)
    );
  }, [allUsers, userSearchTerm]);

  useEffect(() => {
    let resizeTimeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        setCellWidth(calculateCellWidth());
      }, 100);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      clearTimeout(resizeTimeout);
    };
  }, []);

  useEffect(() => {
    setConnected(false);
    setError("");
    if (!currentUserId) return;

    const ref = doc(db, "trackers", currentUserId);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        const docData = snap.data() || {};
        setData(docData);
        setConnected(true);
      },
      (err) => {
        setConnected(false);
        setError(err?.message || "Failed to subscribe.");
      }
    );

    return () => unsub();
  }, [currentUserId]);

  const MODULE_VIDEOS = useMemo(() => {
    if (videosLoading) {
      return isPaid ? MODULE_VIDEOS_PAID : MODULE_VIDEOS_FREE;
    }
    
    if (firebaseVideos) {
      const selectedVideos = isPaid ? firebaseVideos.paid : firebaseVideos.free;
      const defaultVideos = isPaid ? MODULE_VIDEOS_PAID : MODULE_VIDEOS_FREE;
      return {
        ...defaultVideos,
        ...selectedVideos
      };
    }
    
    return isPaid ? MODULE_VIDEOS_PAID : MODULE_VIDEOS_FREE;
  }, [isPaid, firebaseVideos, videosLoading]);

  const unlockedSections = useMemo(() => {
    if (isPaid) {
      return new Set(["start", "philosophy", "followSystem", "buildOutlet"]);
    }
    return new Set(firebaseProgress?.unlockedSections || ['start']);
  }, [firebaseProgress, isPaid]);

  const moduleStates = useMemo(() => {
    const states = {};
    const completedModules = new Set(firebaseProgress?.completedModules || []);
    const videoProgress = firebaseProgress?.videoProgress || {};
    ORDER.forEach((moduleId) => {
      const module = MODULES.find((m) => m.id === moduleId);
      const sectionName = module.section;

      let isComplete = false;
      if (moduleId === "m0") {
        isComplete = completedModules.has(moduleId) || videoProgress["m0_video_0"];
      } else {
        const isCompleteFirestore = computeModuleCompletion(moduleId, data);
        const isCompleteLocal = false;// completedModules.has(moduleId);
        isComplete = isCompleteLocal || isCompleteFirestore;
      }

      const isUnlocked = unlockedSections.has(sectionName);

      states[moduleId] = {
        isComplete,
        isUnlocked,
        hasVideos: !!MODULE_VIDEOS[moduleId]?.length,
        status: isComplete ? "green" : "red",
        isActive: isUnlocked && !isComplete,
      };
    });

    return states;
  }, [firebaseProgress, data, unlockedSections, MODULE_VIDEOS]);

  const completedCount = useMemo(
    () => ORDER.filter((id) => moduleStates[id]?.isComplete).length,
    [moduleStates]
  );

  const progressPct = (completedCount / ORDER.length) * 100;

  // Keep videos and goal labels aligned with their traffic lights by using the same anchor positions
  const getPosition = (index, _isVideo = false) => leftMargin + index * cellWidth;
  const trackW = TOTAL_POSITIONS * cellWidth + leftMargin * 2;
  const trafficRowTop = Math.max(90, Math.round(ROAD_SCENE_HEIGHT * 0.34));
  const carTop = Math.max(trafficRowTop + 120, Math.round(ROAD_SCENE_HEIGHT * 0.74));

  const completedBlocks = useMemo(() => {
    return BLOCKS.reduce((count, block) => {
      const wholeBlockDone = block.every((id) => moduleStates[id]?.isComplete);
      return count + (wholeBlockDone ? 1 : 0);
    }, 0);
  }, [moduleStates]);

  useEffect(() => {
    if (!currentUserId) return;

    const calculateCarPosition = async () => {
      let baseCarIndex = 0;

      if (firebaseProgress) {
        const videoProgress = firebaseProgress.videoProgress || {};
        const completedModules = firebaseProgress.completedModules || [];
        const isIntroCompleted = completedModules.includes("m0") || videoProgress["m0_video_0"];
        
        if (isIntroCompleted) {
          baseCarIndex = 1;
        }
      }

      try {
        let trackerCarIndex = 0;
        if (baseCarIndex > 0) {
          const fourByFourResult = await checkFourByFourStatus(currentUserId);
          const associateResult = await checkAssociateStatus(currentUserId);
          trackerCarIndex = fourByFourResult + associateResult;
        }
        
        const totalCarIndex = baseCarIndex + trackerCarIndex;
        setCarIndex(clamp(totalCarIndex, 0, CAR_ANCHOR_INDEX.length - 1));
      } catch (error) {
        console.error("Error fetching async status:", error);
        setCarIndex(baseCarIndex);
      }
    };

    calculateCarPosition();
  }, [currentUserId, firebaseProgress]);

  const carLeft = useMemo(() => {
    const anchorIndex = CAR_ANCHOR_INDEX[carIndex];
    const baseLeft = leftMargin + anchorIndex * cellWidth;
    // Nudge the car slightly back when it's at the first traffic light (after intro)
    const offset = carIndex === 1 ? Math.round(cellWidth * 0.2) + 25: 0;
    return baseLeft - offset;
  }, [carIndex, cellWidth, leftMargin]);

  const activeModuleId = useMemo(() => {
    return ORDER.find((id) => moduleStates[id]?.isActive) || ORDER[0];
  }, [moduleStates]);

  const handleVideoClick = (moduleId, videoIndex = 0) => {
    if (viewMode === "admin") {
      alert("Admin view is read-only. Switch to Trainee mode to interact.");
      return;
    }

    if (!currentUserId) {
      alert("Please select a user first.");
      return;
    }

    const state = moduleStates[moduleId];

    if (!state.isUnlocked) {
      alert("Complete the previous section first to unlock this module!");
      return;
    }

    // Require the three philosophy modules to be completed before allowing "3 DIRECTS" videos (module m4)
    if (
      moduleId === "m4" &&
      !(moduleStates["m1"]?.isComplete && moduleStates["m2"]?.isComplete && moduleStates["m3"]?.isComplete)
    ) {
      alert("Please complete MULTI-HANDED, 10% 3RULES / 3 GOALS, and SELF IMPROVEMENT first.");
      return;
    }

    const videoProgress = firebaseProgress?.videoProgress || {};
    if (videoIndex > 0 && !videoProgress[`${moduleId}_video_${videoIndex - 1}`]) {
      alert("Please complete the previous video first!");
      return;
    }

    setActiveModule(moduleId);
    setCurrentVideoIndex(videoIndex);
    setVideoWatched(false);
  };

  const handleVideoComplete = async () => {
    if (!activeModule || !currentUserId) return;

    try {
      const videos = MODULE_VIDEOS[activeModule];
      const isLastVideo = currentVideoIndex >= videos.length - 1;

      await onboardingProgressService.markVideoWatched(
        currentUserId,
        activeModule, 
        currentVideoIndex
      );
      const videoProgress = firebaseProgress?.videoProgress || {};
      // Remove the current video progress key from videoProgress
      if (videoProgress.hasOwnProperty(`${activeModule}_video_${currentVideoIndex}`)) {
        delete videoProgress[`${activeModule}_video_${currentVideoIndex}`];
      }
      const changeSection = activeModule === 'm0'
        ? true
        : ['m1', 'm2', 'm3'].includes(activeModule)
        ? !Object.values(videoProgress).includes(false)
        : false;
    //  if (!Object.values(firebaseProgress?.videoProgress || {}).includes(false)) {

      if (changeSection) {
        await onboardingProgressService.completeModule(currentUserId, activeModule);
        const currentSection = MODULES.find(m => m.id === activeModule)?.section;
        await checkAndUnlockNextSection(currentSection);
      }

      setActiveModule(null);
      setCurrentVideoIndex(0);
      setVideoWatched(false);
    } catch (error) {
      console.error('Error saving video progress:', error);
      alert('Failed to save progress. Please try again.');
    }
  };

  const checkAndUnlockNextSection = async (completedSection) => {
    const sectionOrder = ['start', 'philosophy', 'followSystem', 'buildOutlet'];
    const currentIndex = sectionOrder.indexOf(completedSection);
    
    if (currentIndex < sectionOrder.length - 1) {
      const nextSection = sectionOrder[currentIndex + 1];
      await onboardingProgressService.unlockSection(currentUserId, nextSection);
    }
  };

  const handleResetProgress = async () => {
    if (!currentUserId) return;
    
    if (window.confirm(`Reset all progress for ${currentUserName || 'this user'}? This cannot be undone.`)) {
      try {
        await onboardingProgressService.resetProgress(currentUserId);
        alert('Progress reset successfully!');
      } catch (error) {
        console.error('Error resetting progress:', error);
        alert('Failed to reset progress.');
      }
    }
  };

  const handleCheckboxComplete = async (moduleId, checkboxIndex = null) => {
    if (!currentUserId) return;

    const state = moduleStates[moduleId];
    if (!state.isUnlocked) return;

    try {
      if (moduleId === "m4" || moduleId === "m5") {
        await handleMultipleCheckboxCompletion(moduleId, checkboxIndex);
      } else {
        await onboardingProgressService.completeModule(currentUserId, moduleId);
        const moduleSection = MODULES.find(m => m.id === moduleId)?.section;
        await checkAndUnlockNextSection(moduleSection);
      }
    } catch (error) {
      console.error('Error saving checkbox progress:', error);
    }
  };

  const handleMultipleCheckboxCompletion = async (moduleId, checkboxIndex) => {
    await onboardingProgressService.markCheckboxComplete(currentUserId, moduleId, checkboxIndex);
    
    const requiredCheckboxes = 3;
    const checkboxProgress = firebaseProgress?.checkboxProgress || {};
    
    const completedCheckboxes = Array.from({ length: requiredCheckboxes }, (_, i) => 
      checkboxProgress[`${moduleId}_checkbox_${i}`]
    ).filter(Boolean).length;
    
    if (completedCheckboxes >= requiredCheckboxes) {
      await onboardingProgressService.completeModule(currentUserId, moduleId);
      const moduleSection = MODULES.find(m => m.id === moduleId)?.section;
      await checkAndUnlockNextSection(moduleSection);
    }
  };


    useEffect(() => {
      console.log(carIndex);
      setShowCheckboxes(CAR_ANCHOR_INDEX[carIndex] > 4);
    }, [carIndex]);

  return (
    <div className="onboarding-road">
      <style>{CSS}</style>

      {activeModule && (
        <VideoModal
          video={MODULE_VIDEOS[activeModule][currentVideoIndex]}
          onComplete={handleVideoComplete}
          onWatched={() => setVideoWatched(true)}
          onClose={() => {
              setActiveModule(null);
              setCurrentVideoIndex(0);
              setVideoWatched(false);
            // if (
            //   window.confirm(
            //     "Are you sure you want to exit? Your progress on this video won't be saved."
            //   )
            // ) {
            //   setActiveModule(null);
            //   setCurrentVideoIndex(0);
            //   setVideoWatched(false);
            // }
          }}
          canProceed={videoWatched}
          buttonText={
            currentVideoIndex >= MODULE_VIDEOS[activeModule].length - 1
              ? "Complete Module"
              : "Next Video"
          }
        />
      )}

      {trackerModalOpen && (
        <TrackerModal
          moduleId={trackerModalOpen}
          onClose={() => setTrackerModalOpen(null)}
          associateData={data}
        />
      )}

      {prospectTableModal.isOpen && (
        <ProspectsModal
          isOpen={prospectTableModal.isOpen}
          onClose={() => setProspectTableModal({ isOpen: false, filterType: null, userId: null, userName: null, modalSource: null })}
          filterType={prospectTableModal.filterType}
          userId={prospectTableModal.userId}
          userName={prospectTableModal.userName}
          modalSource={prospectTableModal.modalSource}
        />
      )}

      <div className="control-bar">
        <div 
          className="brand reset-button" 
          onClick={handleResetProgress}
          title="Click to reset progress"
        >
          🏆 Onboarding Road (Reset)
        </div>

        <div className="picker wide user-search-container">
          <label htmlFor="user-search">Search User</label>
          <input
            id="user-search"
            name="user-search"
            type="text"
            value={userSearchTerm}
            onChange={(e) => {
              setUserSearchTerm(e.target.value);
              setShowUserDropdown(true);
            }}
            onFocus={() => setShowUserDropdown(true)}
            placeholder="Type name or email..."
          />
          {showUserDropdown && filteredUsers.length > 0 && (
            <div className="user-dropdown">
              {filteredUsers.slice(0, 10).map((user) => (
                <div
                  key={user.uid}
                  className="user-dropdown-item"
                  onClick={async () => {
                    setUserSearchTerm(`${user.name} (${user.email})`);
                    setSelectedUserId(user.uid);
                    setSelectedUserName(user.name);
                    setShowUserDropdown(false);
                  }}
                >
                  <div className="user-dropdown-name">{user.name}</div>
                  <div className="user-dropdown-email">{user.email}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="status">
          <span
            className={`dot ${connected ? "ok" : error ? "bad" : "idle"}`}
          />
          {connected ? "Live" : error ? "Error" : "Connecting…"}
        </div>

        <div className="progress">
          <div className="bar" style={{ width: `${progressPct}%` }} />
          <span>
            {completedCount}/{ORDER.length} Complete
          </span>
        </div>
      </div>

      <div className="main-content-container">
        <div className="road-scene-section">
          <div
            className="road-scene"
            style={{
              height: ROAD_SCENE_HEIGHT,
              backgroundImage: "url('./road.png')",
              width: trackW,
              minWidth: trackW,
              margin: "0 auto",
            }}
          >
            {SECTIONS.map((section) => {
              const sectionCenter = (section.startIndex + section.endIndex) / 2;
              const firstModuleInSection = MODULES.find(
                (m) => MODULE_POSITIONS[m.id] === section.startIndex
              );
              const isUnlocked = firstModuleInSection
                ? moduleStates[firstModuleInSection.id]?.isUnlocked
                : true;

              return (
                <div
                  key={section.name}
                  className={`section-header ${!isUnlocked ? "locked" : ""}`}
                  style={{ left: getPosition(sectionCenter) }}
                >
                  <div
                    className={`section-title ${section.nameClass || ""} ${
                      section.badge?.type ? `title-${section.badge.type}` : ""
                    }`}
                  >
                    {section.name.split(" ").map((word, i) => (
                      <div key={i}>{word}</div>
                    ))}
                  </div>

                  {section.badge && (
                    <div
                      className={`section-badge ${
                        section.badge.className || ""
                      } ${
                        section.badge.type ? `badge-${section.badge.type}` : ""
                      }`}
                    >
                      <img src={section.badge.image} alt={section.name} />
                    </div>
                  )}
                </div>
              );
            })}

            <div className="smd-badge" style={{ left: getPosition(15.5) }}>
              <div className="smd-pill">
                <img src="./smd100k.png" alt="smd100k" />
              </div>
            </div>

            <div className="traffic-lights-row" style={{ top: trafficRowTop }}>
              {MODULES.filter(m => m.id !== "m0").map((module) => {
                const position = MODULE_POSITIONS[module.id];
                const state = moduleStates[module.id];
                const isLocked = !state?.isUnlocked;
                const isComplete = state?.isComplete;

                return (
                  <div
                    key={module.id}
                    className="traffic-light-container"
                    style={{ left: getPosition(position) }}
                    title={
                      isLocked
                        ? "Locked – finish previous step"
                        : isComplete
                        ? "Completed"
                        : "Available – click video"
                    }
                  >
                    {state?.isUnlocked && (
                      <div className="module-label">
                        {module.title === "10% / 3 Rules / 3 Goals" ? (
                          <>
                            10% 3RULES<br/>3 GOALS
                          </>
                        ) : module.title === "1 Direct Registration" ? (
                          <>
                            1 DIRECT<br/>REGISTRATION
                          </>
                        ) : module.title === "45,000 Personal Points" ? (
                          <>
                            45K PERSONAL<br/>POINTS
                          </>
                        ) : module.title === "15 Registrations on Base" ? (
                          <>
                            15 REGISTRATION<br/>BASE
                          </>
                        ) : module.title === "Observe 4 Recruits" ? (
                          "3 DIRECTS"
                        ) : module.title === "Observe 4 Clients" ? (
                          "3 CLIENTS"
                        ) : module.title === "Get License" ? (
                          "GET LICENSED"
                        ) : module.title === "9 Recruits" ? (
                          "9 RECRUITS"
                        ) : module.title === "3 Licenses" ? (
                          "3 LICENSES"
                        ) : module.title === "Multi Handed" ? (
                          "MULTI-HANDED"
                        ) : module.title === "Self Improvement" ? (
                          "SELF IMPROVEMENT"
                        ) : (
                          module.title.toUpperCase()
                        )}
                      </div>
                    )}
                    <img
                      src={
                        state.isComplete
                          ? "./traffic-light-green.png"
                          : "./traffic-light-red.png"
                      }
                      alt={`${module.title} traffic light`}
                      className="traffic-light-image"
                    />
                  </div>
                );
              })}
            </div>

            <div className="car" style={{ left: carLeft, top: carTop }}>
              <img src="./car.png" alt="Progress car" className="car-image" />
            </div>
          </div>
        </div>

        <div className="video-section">
          <div
            className="video-dock"
            style={{ width: trackW, height: VIDEO_DOCK_HEIGHT }}
          >
            <div
              className="video-start"
              style={{ left: getPosition(MODULE_POSITIONS.m0, true) }}
            >
              <h1 className="video-start-title">START</h1>
              <img src="./start.png" alt="Start" className="video-start-image" />
            </div>
            
            {MODULES.map((module) => {
              const videos = MODULE_VIDEOS[module.id];
              if (!videos) return null;

              const position = MODULE_POSITIONS[module.id] !== 0 ? MODULE_POSITIONS[module.id] : 1;
              const state = moduleStates[module.id];
              const videoProgress = firebaseProgress?.videoProgress || {};

              if (!unlockedSections.has(module.section)) return null;
              return (
                <div
                  key={module.id}
                  className="video-column"
                  style={{ left: getPosition(position, true) }}
                >
                  {videos.map((_, videoIndex) => {
                    // For "3 DIRECTS" (module m4), require m1, m2, and m3 to be completed before any video is playable
                    const prerequisitesOk =
                      module.id !== "m4" ||
                      (moduleStates["m1"]?.isComplete && moduleStates["m2"]?.isComplete && moduleStates["m3"]?.isComplete);

                    const canPlayVideo =
                      state?.isUnlocked &&
                      (videoIndex === 0 || videoProgress[`${module.id}_video_${videoIndex - 1}`]);

                    const isVideoComplete = videoProgress[`${module.id}_video_${videoIndex}`];
                    const label = videos.length > 1 ? `Part ${videoIndex + 1}` : "1 video";
                    if (!prerequisitesOk) {
                    return;
                    }
                    return (
                      <button
                        key={videoIndex}
                        className={`video-tile 
                          ${module.id === "m0" && !isVideoComplete ? "intro-highlight" : ""} 
                          ${!canPlayVideo ? "locked" : ""} 
                          ${isVideoComplete ? "completed" : ""}`}
                        onClick={() => handleVideoClick(module.id, videoIndex)}
                        disabled={!canPlayVideo || viewMode === "admin"}
                        aria-label={`${module.title} - ${label}`}
                      >
                        <div className="play-icon">
                          {isVideoComplete ? "✓" : "▶"}
                        </div>
                        <div className="video-label">{label}</div>
                      </button>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>

        <div className="checkbox-section">
  <div
    className="checkbox-content"
    style={{ width: trackW, height: CHECKBOX_SECTION_HEIGHT }}
  >
    <div className="agent-name">{selectedUserName || data?.displayName || user.displayName || data?.email || "AGENT NAME"}</div>

    {/* Philosophy Checkboxes */}
    {[
      { id: "m1", label: "MULTI-HANDED" },
      { id: "m2", label: "10% 3RULES\n3 GOALS" },
      { id: "m3", label: "SELF IMPROVEMENT" }
    ].map((item) => {
      const state = moduleStates[item.id];
      if (!state?.isUnlocked) return null;
      return (
        <div 
          key={item.id}
          className="individual-checkbox-container"
          style={{ left: getPosition(MODULE_POSITIONS[item.id], true) }}
        >
          <div className="checkbox-label">
            {item.label.split('\n').map((line, i) => (
              <React.Fragment key={i}>
                {line}
                {i < item.label.split('\n').length - 1 && <br />}
              </React.Fragment>
            ))}
          </div>
          <input 
            type="checkbox" 
            checked={state?.isComplete}
            readOnly
            className="aligned-checkbox"
          />
        </div>
      );
    })}

    {/* Follow System Checkboxes */}
    {[
      { id: "m4", label: "3 DIRECTS", hasMultiple: true },
      { id: "m5", label: "3 CLIENTS", hasMultiple: true },
      { id: "m6", label: "GET LICENSED", hasMultiple: false },
      { id: "m7", label: "1 DIRECT\nREGISTRATION", hasMultiple: false }
    ].map((item) => {
      const state = moduleStates[item.id];
      if (!state?.isUnlocked) return null;
      
      const checkboxProgress = firebaseProgress?.checkboxProgress || {};
      
      return (
        <div 
          key={item.id}
          className="individual-checkbox-container"
          style={{ left: getPosition(MODULE_POSITIONS[item.id], true) }}
        >
          <div className="checkbox-label">
            {item.label.split('\n').map((line, i) => (
              <React.Fragment key={i}>
                {line}
                {i < item.label.split('\n').length - 1 && <br />}
              </React.Fragment>
            ))}
          </div>
          {showCheckboxes && <div className="checkbox-column">
            {item.hasMultiple ? (
              <>
                {[0, 1, 2].map((checkboxIndex) => {
                  const isChecked = state?.isComplete
                    ? true
                    : checkboxProgress[`${item.id}_checkbox_${checkboxIndex}`] || false;
                  return (
                    <input 
                      key={checkboxIndex}
                      type="checkbox" 
                      checked={isChecked}
                      onChange={() => handleCheckboxComplete(item.id, checkboxIndex)}
                      className="aligned-checkbox"
                      disabled={state?.isComplete}
                    />
                  );
                })}
              </>
            ) : (
              <input 
                type="checkbox" 
                checked={state?.isComplete || false}
                onChange={() => handleCheckboxComplete(item.id)}
                className="aligned-checkbox"
                disabled={state?.isComplete}
              />
            )}
          </div>}
        </div>
      );
    })}

    {/* Rolling 3 Months Section - CORRECTED */}
    {unlockedSections.has("followSystem") && (
      <>
        <div className="rolling-section-header" style={{ 
          left: (getPosition(MODULE_POSITIONS.m9, true) + getPosition(MODULE_POSITIONS.m10, true)) / 2,
          top: '-30px'
        }}>
          ROLLING 3 MONTHS
        </div>

        {[
          { 
            id: "m8", 
            label: "9 RECRUITS", 
            value: trackerData.recruitsTtl,
            target: 9,
            field: "recruitsTtl"
          },
          { 
            id: "m9", 
            label: "45K PERSONAL\nPOINTS", 
            value: trackerData.personalPoints,
            target: 45000,
            field: "personalPoints"
          },
          { 
            id: "m10", 
            label: "3 LICENSES", 
            value: trackerData.licensesTtl,
            target: 3,
            field: "licensesTtl"
          },
          { 
            id: "m11", 
            label: "15 REGISTRATION\nBASE", 
            value: trackerData.registrationsBase,
            target: 15,
            field: "registrationsBase"
          }
        ].map((item) => {
          const state = moduleStates[item.id];
          const isCompleted = item.value >= item.target;
          
          return (
            <div 
              key={item.id}
              className="individual-checkbox-container"
              style={{ left: getPosition(MODULE_POSITIONS[item.id], true) }}
            >
              <div className="checkbox-label">
                {item.label.split('\n').map((line, i) => (
                  <React.Fragment key={i}>
                    {line}
                    {i < item.label.split('\n').length - 1 && <br />}
                  </React.Fragment>
                ))}
              </div>
              
              {plan !== ACCOUNT_TYPES.NEW_AGENT && <div className="value-input-container">
                <input
                  type="text"
                  value={item.value.toLocaleString()}
                  readOnly
                  className={`value-input ${isCompleted ? 'completed' : 'in-progress'}`}
                  onClick={() => {
                    if (item.id === "m8") {
                      setProspectTableModal({
                        isOpen: true,
                        filterType: 'allProspects',
                        userId: currentUserId,
                        userName: selectedUserName || data?.name || data?.email || "User",
                        modalSource: 'recruits'
                      });
                    } else if (item.id === "m9") {
                      setProspectTableModal({
                        isOpen: true,
                        filterType: 'clientsOnly',
                        userId: currentUserId,
                        userName: selectedUserName || data?.name || data?.email || "User",
                        modalSource: 'production'
                      });
                    } else if (item.id === "m10") {
                      setProspectTableModal({
                        isOpen: true,
                        filterType: 'recruitsOnly',
                        userId: currentUserId,
                        userName: selectedUserName || data?.name || data?.email || "User",
                        modalSource: 'licenses'
                      });
                    } else {
                      setTrackerModalOpen(item.id);
                    }
                  }}
                />
              </div>}
            </div>
          );
        })}
      </>
    )}
  </div>
</div>
      </div>
    </div>
  );
}


function VideoModal({
  video,
  onComplete,
  onWatched,
  onClose,
  canProceed,
  buttonText = "Continue",
}) {
  const [timeWatched, setTimeWatched] = useState(0);
  const [videoDuration, setVideoDuration] = useState(0);

  useEffect(() => {
    setVideoDuration(10);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeWatched((t) => t + 1);
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      onWatched();
    }, 2000);
    return () => clearTimeout(timer);
  }, [onWatched]);

  const watchProgress = 100;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>
          ✕
        </button>

        <h2>{video.title}</h2>
        <p className="video-desc">{video.description}</p>

        <div className="video-container">
          <VideoPlayer
            src={video.url}
            title={video.title}
            className="onboarding-modal-video"
            controls
            autoPlay
          />
        </div>

        <div className="watch-indicator">
          <div className="watch-bar">
            <div
              className="watch-fill"
              style={{ width: `${watchProgress}%` }}
            />
          </div>
          {/* <span>
            ✓ Video ready - Click button below to mark as complete
          </span> */}
        </div>

        <button
          className="modal-btn"
          onClick={onComplete}
        >
          {buttonText}
        </button>
      </div>
    </div>
  );
}

/* ------------------------ PROSPECTS MODAL COMPONENT ------------------------ */

function uniqueId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID)
    return crypto.randomUUID();
  return `id_${Date.now()}_${Math.round(Math.random() * 1e9)}`;
}

function formatDateTimePretty(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const date = d.toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "2-digit",
  });
  const time = d
    .toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })
    .toLowerCase();
  return `${date} at ${time}`;
}

function tsToMs(v) {
  if (!v) return 0;
  if (typeof v?.toMillis === "function") return v.toMillis();
  if (v instanceof Date) return v.getTime();
  const t = new Date(v).getTime();
  return Number.isFinite(t) ? t : 0;
}

function formatDateTimeISO(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const date = d.toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "2-digit",
  });
  const time = d.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
  });
  return `${date} ${time}`;
}

function ProspectsModal({ isOpen, onClose, filterType, userId, userName, modalSource }) {
  const [prospects, setProspects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: "name", direction: "asc" });
  const [colWidths, setColWidths] = useState({
    mark: 42,
    leader: 180,
    recruiter: 180,
    name: 240,
    top25: 90,
    hot: 80,
    outcome: 150,
    profile: 520,
    notes: 520,
    actions: 180
  });
  
  const [notesOpenFor, setNotesOpenFor] = useState(null);
  const [noteDrafts, setNoteDrafts] = useState({});
  const [noteSaving, setNoteSaving] = useState(false);
  const [noteDeletingId, setNoteDeletingId] = useState(null);
  const [focusedNoteInputId, setFocusedNoteInputId] = useState(null);
  
  const dragRef = useRef(null);
  
  useEffect(() => {
    const onMove = (e) => {
      if (!dragRef.current) return;
      const { key, startX, startW } = dragRef.current;
      const dx = e.clientX - startX;
      const next = Math.max(80, startW + dx);
      setColWidths((w) => ({ ...w, [key]: next }));
    };
    const onUp = () => {
      dragRef.current = null;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, []);

  const startDrag = (key, e) => {
    dragRef.current = { key, startX: e.clientX, startW: colWidths[key] || 140 };
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  };
  
  const prospectsRef = (id) => doc(db, "prospects", id);
  
  const addNoteInternal = async (rid, baseNotes, text) => {
    const ref = prospectsRef(rid);
    const now = new Date();
    const nowIso = now.toISOString();
    const nowTimestamp = now.getTime();
    const recruiterName = "Admin";
    const newNote = {
      id: uniqueId(),
      text: text.trim(),
      createdAt: nowTimestamp,
      createdAtISO: nowIso,
      createdBy: recruiterName,
    };
    
    setProspects(prev => prev.map(p => {
      if (p.id === rid) {
        const next = Array.isArray(p.notes) ? [...p.notes, newNote] : [newNote];
        return { ...p, notes: next };
      }
      return p;
    }));
    
    await setDoc(
      ref,
      { notes: [...(baseNotes || []), newNote], updatedAt: serverTimestamp() },
      { merge: true }
    );
  };

  const addInlineNote = async (prospect) => {
    const text = (noteDrafts[prospect.id] || "").trim();
    if (!text) return;
    try {
      setNoteSaving(true);
      const base = Array.isArray(prospect.notes) ? prospect.notes : [];
      await addNoteInternal(prospect.id, base, text);
      setNoteDrafts((d) => ({ ...d, [prospect.id]: "" }));
    } catch (e) {
      console.error(e);
    } finally {
      setNoteSaving(false);
    }
  };

  const deleteNote = async (prospect, noteId) => {
    if (!prospect) return;
    setNoteDeletingId(noteId);
    try {
      const ref = prospectsRef(prospect.id);
      
      setProspects(prev => prev.map(p => {
        if (p.id === prospect.id) {
          return { ...p, notes: (p.notes || []).filter((n) => n.id !== noteId) };
        }
        return p;
      }));
      
      const base = Array.isArray(prospect.notes) ? prospect.notes : [];
      await setDoc(
        ref,
        {
          notes: base.filter((n) => n.id !== noteId),
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
    } catch (e) {
      console.error(e);
    } finally {
      setNoteDeletingId(null);
    }
  };

  useEffect(() => {
    if (!isOpen) {
      setProspects([]);
      return;
    }

    setLoading(true);
    
    const q = query(
      collection(db, "prospects"),
      where("status", "!=", "inactive")
    );

    const unsubscribe = onSnapshot(
      q,
      { includeMetadataChanges: false },
      (snapshot) => {
        let data = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        if ((filterType === 'allProspects' || filterType === 'recruitsOnly' || filterType === 'clientsOnly') && userId) {
          data = data.filter(p => {
            const matchesRecruiterById = p.recruiterId === userId;
            const matchesRecruiterByName = p.recruiter === userName;
            const matchesRecruiter = matchesRecruiterById || (matchesRecruiterByName && (!p.recruiterId || p.recruiterId === 'undefined'));
            
            if (filterType === 'allProspects') {
              return matchesRecruiter;
            }
            
            if (filterType === 'clientsOnly') {
              const outcome = (p.outcome || "").toLowerCase();
              const matchesOutcome = !outcome || outcome === 'client' || outcome === 'both';
              return matchesRecruiter && matchesOutcome;
            }
            
            if (filterType === 'recruitsOnly') {
              const outcome = (p.outcome || "").toLowerCase();
              const matchesOutcome = outcome === 'recruit' || outcome === 'both';
              return matchesRecruiter && matchesOutcome;
            }
            
            return false;
          });
        }
        
        setProspects(data);
        setLoading(false);
      },
      (error) => {
        console.error("Error loading prospects:", error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [isOpen, filterType, userId, userName]);

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return '⇅';
    return sortConfig.direction === 'asc' ? '▲' : '▼';
  };

  const filteredProspects = useMemo(() => {
    let filtered = prospects;
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(p => 
        (p.name?.toLowerCase() || "").includes(term) ||
        (p.leader?.toLowerCase() || "").includes(term) ||
        (p.recruiter?.toLowerCase() || "").includes(term) ||
        (p.email?.toLowerCase() || "").includes(term) ||
        (p.phone?.toLowerCase() || "").includes(term)
      );
    }

    if (sortConfig.key) {
      filtered.sort((a, b) => {
        const aVal = a[sortConfig.key] || "";
        const bVal = b[sortConfig.key] || "";
        if (sortConfig.direction === 'asc') {
          return aVal > bVal ? 1 : -1;
        }
        return aVal < bVal ? 1 : -1;
      });
    }

    return filtered;
  }, [prospects, searchTerm, sortConfig]);
  
  const currentNotesForOpenRow = useMemo(() => {
    if (!notesOpenFor) return [];
    const live = prospects.find((r) => r.id === notesOpenFor.id) || notesOpenFor;
    return Array.isArray(live.notes) ? live.notes : [];
  }, [notesOpenFor, prospects]);

  const sortedModalNotes = useMemo(() => {
    const copy = [...currentNotesForOpenRow];
    copy.sort((a, b) => tsToMs(a.createdAt) - tsToMs(b.createdAt));
    return copy;
  }, [currentNotesForOpenRow]);

  if (!isOpen) return null;

  let title = `All Prospects (${filteredProspects.length})`;
  if (filterType === 'allProspects' && userId && userName) {
    title = `${userName}'s Prospects (${filteredProspects.length})`;
  } else if (filterType === 'clientsOnly' && userId && userName) {
    title = `${userName}'s Personal Points - Clients (${filteredProspects.length})`;
  } else if (filterType === 'recruitsOnly' && userId && userName) {
    title = `${userName}'s Licenses - Recruits (${filteredProspects.length})`;
  }
  
  const getRowColors = (prospect) => {
    const mark = (prospect.mark || "default").toLowerCase();
    switch (mark) {
      case "client":
        return { 
          bg: "rgba(0, 179, 92, 0.15)", 
          bgSolid: "#004d2e",
          border: "rgba(0, 230, 118, 0.3)" 
        };
      case "recruit":
        return { 
          bg: "rgba(35, 102, 217, 0.15)", 
          bgSolid: "#0d2956",
          border: "rgba(41, 121, 255, 0.3)" 
        };
      case "both":
        return { 
          bg: "rgba(204, 143, 0, 0.15)", 
          bgSolid: "#5c3d00",
          border: "rgba(255, 179, 0, 0.3)" 
        };
      default:
        return { 
          bg: "#1a1a1f",
          bgSolid: "#1a1a1f",
          border: "rgba(42, 46, 55, 1)" 
        };
    }
  };

  return (
    <>
      <div className="modal-overlay-glass" onClick={onClose}>
        <div className="prospects-modal-glass-v2" onClick={(e) => e.stopPropagation()}>
          <div className="prospects-modal-header-glass">
            <h2>{title}</h2>
            <button className="prospects-modal-close-glass" onClick={onClose}>✕</button>
          </div>

          <div className="prospects-modal-search">
            <input
              type="text"
              placeholder="Search prospects..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="prospects-search-input-glass"
            />
          </div>

          <div className="prospects-table-wrapper-resizable">
            {loading ? (
              <div className="prospects-loading-glass">Loading...</div>
            ) : filteredProspects.length === 0 ? (
              <div className="prospects-empty-glass-v2">
                {searchTerm ? "No matching prospects found" : "No prospects yet"}
              </div>
            ) : (
              <div className="prospects-table-scroll">
                <table className="prospects-table-resizable">
                  <thead>
                    <tr>
                      <th style={{ width: colWidths.mark, minWidth: colWidths.mark, position: 'relative' }}>
                        <span className="resize-handle" onMouseDown={(e) => startDrag("mark", e)} />
                      </th>
                      <th 
                        onClick={() => handleSort("leader")} 
                        style={{ width: colWidths.leader, minWidth: colWidths.leader, position: 'relative' }}
                      >
                        Leader {getSortIcon("leader")}
                        <span className="resize-handle" onMouseDown={(e) => startDrag("leader", e)} />
                      </th>
                      <th 
                        onClick={() => handleSort("recruiter")} 
                        style={{ width: colWidths.recruiter, minWidth: colWidths.recruiter, position: 'relative' }}
                      >
                        Recruiter {getSortIcon("recruiter")}
                        <span className="resize-handle" onMouseDown={(e) => startDrag("recruiter", e)} />
                      </th>
                      <th 
                        onClick={() => handleSort("name")} 
                        style={{ width: colWidths.name, minWidth: colWidths.name, position: 'relative' }}
                      >
                        Name {getSortIcon("name")}
                        <span className="resize-handle" onMouseDown={(e) => startDrag("name", e)} />
                      </th>
                      <th style={{ width: colWidths.top25, minWidth: colWidths.top25, textAlign: 'center', position: 'relative' }}>
                        Top 25
                        <span className="resize-handle" onMouseDown={(e) => startDrag("top25", e)} />
                      </th>
                      <th style={{ width: colWidths.hot, minWidth: colWidths.hot, textAlign: 'center', position: 'relative' }}>
                        HOT
                        <span className="resize-handle" onMouseDown={(e) => startDrag("hot", e)} />
                      </th>
                      <th onClick={() => handleSort("outcome")} style={{ width: colWidths.outcome, minWidth: colWidths.outcome, position: 'relative' }}>
                        Outcome {getSortIcon("outcome")}
                        <span className="resize-handle" onMouseDown={(e) => startDrag("outcome", e)} />
                      </th>
                      <th style={{ width: colWidths.profile, minWidth: colWidths.profile, position: 'relative' }}>
                        Profile
                        <span className="resize-handle" onMouseDown={(e) => startDrag("profile", e)} />
                      </th>
                      <th style={{ width: colWidths.notes, minWidth: colWidths.notes, position: 'relative' }}>
                        Notes
                        <span className="resize-handle" onMouseDown={(e) => startDrag("notes", e)} />
                      </th>
                      <th style={{ width: colWidths.actions, minWidth: colWidths.actions, position: 'relative' }}>
                        Actions
                        <span className="resize-handle" onMouseDown={(e) => startDrag("actions", e)} />
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredProspects.map((prospect) => {
                      const colors = getRowColors(prospect);
                      const isNoteFocused = focusedNoteInputId === prospect.id;
                      const notesArr = Array.isArray(prospect.notes) ? prospect.notes : [];
                      const lastNote = notesArr.length > 0
                        ? notesArr.slice().reverse().find(n => n.text)
                        : null;
                      const inputValue = isNoteFocused 
                        ? (noteDrafts[prospect.id] || "")
                        : (noteDrafts[prospect.id] || (lastNote?.text || ""));
                      
                      return (
                        <tr 
                          key={prospect.id}
                          style={{
                            backgroundColor: colors.bg,
                            borderLeft: `3px solid ${colors.border}`
                          }}
                        >
                          <td style={{ width: colWidths.mark, textAlign: 'center' }}>
                            <div 
                              className="prospect-marker-inline" 
                              style={{ 
                                backgroundColor: prospect.mark === 'client' ? '#00E676' :
                                               prospect.mark === 'recruit' ? '#2979FF' :
                                               prospect.mark === 'both' ? '#FFB300' : '#cfcfcf'
                              }}
                            />
                          </td>
                          <td style={{ width: colWidths.leader }}>
                            {prospect.leader || "—"}
                          </td>
                          <td style={{ width: colWidths.recruiter }}>
                            {prospect.recruiter || "—"}
                          </td>
                          <td style={{ width: colWidths.name }}>
                            {prospect.name || "—"}
                          </td>
                          <td style={{ width: colWidths.top25, textAlign: 'center' }}>
                            {prospect.top25 ? "✓" : "—"}
                          </td>
                          <td style={{ width: colWidths.hot, textAlign: 'center' }}>
                            {prospect.hot ? "✓" : "—"}
                          </td>
                          <td style={{ width: colWidths.outcome }}>
                            {prospect.outcome ? (
                              <span className={`outcome-badge-inline ${prospect.outcome?.toLowerCase() || ""}`}>
                                {prospect.outcome}
                              </span>
                            ) : "—"}
                          </td>
                          <td style={{ width: colWidths.profile, fontSize: '12px' }}>
                            {(() => {
                              const p = prospect.profile || {};
                              const age = p.birthday ? (() => {
                                const birthDate = new Date(p.birthday);
                                const today = new Date();
                                let age = today.getFullYear() - birthDate.getFullYear();
                                const m = today.getMonth() - birthDate.getMonth();
                                if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) age--;
                                return age >= 0 ? age : null;
                              })() : null;
                              
                              const parts = [
                                p.howKnown,
                                p.relationship || p.relationship === 0 ? `${p.relationship}/10` : null,
                                p.occupation,
                                p.relationshipStatus,
                                p.dependentChildren,
                                age !== null ? `Age: ${age}` : null,
                              ].filter(x => x !== null && x !== undefined && x !== "");
                              
                              return parts.length > 0 ? parts.join(" • ") : "—";
                            })()}
                          </td>
                          <td style={{ width: colWidths.notes }}>
                            <div className="notes-cell-inline">
                              <input
                                className={`note-input-inline ${!isNoteFocused && lastNote && !noteDrafts[prospect.id] ? "showing-existing" : ""}`}
                                type="text"
                                placeholder="Add a note..."
                                value={inputValue}
                                onChange={(e) =>
                                  setNoteDrafts((d) => ({ ...d, [prospect.id]: e.target.value }))
                                }
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" && (noteDrafts[prospect.id] || "").trim()) {
                                    addInlineNote(prospect);
                                  }
                                }}
                                onFocus={() => {
                                  setFocusedNoteInputId(prospect.id);
                                  if (!noteDrafts[prospect.id]) {
                                    setNoteDrafts((d) => ({ ...d, [prospect.id]: "" }));
                                  }
                                }}
                                onBlur={() => setFocusedNoteInputId(null)}
                              />
                              <button
                                className="notes-openall-btn"
                                onClick={() => setNotesOpenFor(prospect)}
                                title="View all notes"
                              >
                                ✏️
                              </button>
                            </div>
                            {!isNoteFocused && lastNote && !noteDrafts[prospect.id] && (
                              <div className="note-meta-inline">
                                {lastNote.createdBy || "—"} • {
                                  lastNote.createdAtISO
                                    ? formatDateTimePretty(lastNote.createdAtISO)
                                    : tsToMs(lastNote.createdAt)
                                    ? formatDateTimePretty(
                                        new Date(tsToMs(lastNote.createdAt)).toISOString()
                                      )
                                    : ""
                                }
                              </div>
                            )}
                          </td>
                          <td style={{ width: colWidths.actions }}>
                            <div className="actions-inline-btns">
                              {prospect.phone && (
                                <a href={`tel:${prospect.phone}`} className="action-btn-inline" title="Call">
                                  📞
                                </a>
                              )}
                              {prospect.email && (
                                <a href={`mailto:${prospect.email}`} className="action-btn-inline" title="Email">
                                  ✉️
                                </a>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {!loading && filteredProspects.length > 0 && (
            <div className="prospects-modal-footer-glass">
              Showing {filteredProspects.length} prospect{filteredProspects.length !== 1 ? "s" : ""}
            </div>
          )}
        </div>
      </div>

      {notesOpenFor && (
        <div className="modal-backdrop" onClick={() => setNotesOpenFor(null)}>
          <div
            className="modal-card"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
          >
            <div className="modal-head">
              <h3>Notes — {notesOpenFor.name}</h3>
              <button
                className="btn ghost"
                onClick={() => setNotesOpenFor(null)}
              >
                ✕
              </button>
            </div>
            <div className="notes-list-modal">
              {sortedModalNotes.length === 0 && (
                <div className="empty-notes">No notes yet.</div>
              )}
              {sortedModalNotes.map((n) => (
                <div key={n.id} className="note-row-modal">
                  <div className="note-text-modal">{n.text}</div>
                  <div className="note-meta-modal">
                    {n.createdBy || "—"} •{" "}
                    {n.createdAtISO
                      ? formatDateTimeISO(n.createdAtISO)
                      : tsToMs(n.createdAt)
                      ? formatDateTimeISO(
                          new Date(tsToMs(n.createdAt)).toISOString()
                        )
                      : ""}
                  </div>
                  <button
                    className="note-delete-modal"
                    onClick={() => deleteNote(notesOpenFor, n.id)}
                    disabled={noteDeletingId === n.id}
                    title="Delete note"
                  >
                    {noteDeletingId === n.id ? "…" : "Delete"}
                  </button>
                </div>
              ))}
            </div>
            <div className="notes-modal-add">
              <textarea
                className="input"
                rows="2"
                placeholder="Add a new note..."
                value={noteDrafts["modal"] || ""}
                onChange={(e) =>
                  setNoteDrafts((d) => ({ ...d, modal: e.target.value }))
                }
              ></textarea>
              <button
                className="btn primary"
                onClick={() => {
                  const text = (noteDrafts["modal"] || "").trim();
                  if (text) {
                    addNoteInternal(
                      notesOpenFor.id,
                      notesOpenFor.notes,
                      text
                    );
                    setNoteDrafts((d) => ({ ...d, modal: "" }));
                  }
                }}
              >
                Add Note
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/* ------------------------ TRACKER MODAL COMPONENT ------------------------ */

function TrackerModal({ moduleId, onClose, associateData }) {
  const [loading, setLoading] = useState(true);
  const [trackerData, setTrackerData] = useState(null);
  const userId = associateData?.id || associateData?.email; 

  useEffect(() => {
    const fetchTrackerData = async () => {
      if (!userId) return;
      
      setLoading(true);
      try {
        let data = { type: "", title: "", current: 0, target: 0, items: [] };

        switch (moduleId) {
          case "m8":
            data.type = "recruits";
            data.title = "9 Recruits Progress";
            data.target = 9;
            
            const recruitsQuery = query(
      collection(db, "prospects"),
      where("recruiterId", "==", userId),
      where("status", "!=", "inactive")
    );
            const recruitsSnap = await getDocs(recruitsQuery);
            data.items = recruitsSnap.docs.map(doc => ({
              name: doc.data().name || "N/A",
              email: doc.data().email || "N/A",
              status: doc.data().status || "Active",
              phone: doc.data().phone || "N/A"
            }));
            data.current = data.items.length;
            break;

          case "m9":
            data.type = "points";
            data.title = "Personal Points Progress";
            data.target = 45000;
            data.current = associateData?.associate?.personalPoints || 0;
            break;

          case "m10":
            data.type = "licenses";
            data.title = "Licenses Progress";
            data.target = 3;
            
            const licensesQuery = query(
      collection(db, "prospects"),
      where("recruiterId", "==", userId),
      where("status", "!=", "inactive")
    );
            const licensesSnap = await getDocs(licensesQuery);
            data.items = licensesSnap.docs.map(doc => ({
              name: doc.data().name || "N/A",
              email: doc.data().email || "N/A",
              licenseType: doc.data().licenseType || "N/A",
              status: doc.data().status || "Active"
            }));
            data.current = data.items.length;
            break;

          case "m11":
            data.type = "registrations";
            data.title = "Base Registrations Progress";
            data.target = 15;
            
            const regsQuery = query(
              collection(db, "prospect_tracker"),
              where("recruiterId", "==", userId),
              where("registeredBase", "==", true)
            );
            const regsSnap = await getDocs(regsQuery);
            data.items = regsSnap.docs.map(doc => ({
              name: doc.data().name || "N/A",
              email: doc.data().email || "N/A",
              phone: doc.data().phone || "N/A",
              date: doc.data().baseRegistrationDate || "N/A"
            }));
            data.current = data.items.length;
            break;

          default:
            break;
        }

        setTrackerData(data);
      } catch (err) {
        console.error("Error fetching tracker data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchTrackerData();
  }, [moduleId, userId, associateData]);

  if (loading) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="tracker-modal-content" onClick={(e) => e.stopPropagation()}>
          <button className="modal-close" onClick={onClose}>×</button>
          <div style={{ textAlign: "center", padding: "40px", color: "#fff" }}>
            Loading tracker data...
          </div>
        </div>
      </div>
    );
  }

  if (!trackerData) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="tracker-modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>
          ×
        </button>

        <h2 className="tracker-modal-title">{trackerData.title}</h2>

        <div className="tracker-progress-bar">
          <div className="tracker-progress-label">
            {trackerData.current} / {trackerData.target}
          </div>
          <div className="tracker-progress-fill-container">
            <div
              className="tracker-progress-fill"
              style={{
                width: `${Math.min((trackerData.current / trackerData.target) * 100, 100)}%`
              }}
            />
          </div>
        </div>

        <div className="tracker-data-list">
          {trackerData.type === "recruits" && (
            <>
              {trackerData.items.length > 0 ? (
                <table className="tracker-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {trackerData.items.map((recruit, idx) => (
                      <tr key={idx}>
                        <td>{recruit.name}</td>
                        <td>{recruit.email}</td>
                        <td>{recruit.phone}</td>
                        <td>{recruit.status}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="tracker-empty">No recruits yet</p>
              )}
            </>
          )}

          {trackerData.type === "points" && (
            <div className="tracker-points-display">
              <div className="tracker-big-number">{trackerData.current.toLocaleString()}</div>
              <p className="tracker-points-label">Personal Points</p>
              <p className="tracker-points-remaining">
                {trackerData.target - trackerData.current > 0
                  ? `${(trackerData.target - trackerData.current).toLocaleString()} points to go`
                  : "Goal achieved! 🎉"}
              </p>
            </div>
          )}

          {trackerData.type === "licenses" && (
            <>
              {trackerData.items.length > 0 ? (
                <table className="tracker-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>License Type</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {trackerData.items.map((license, idx) => (
                      <tr key={idx}>
                        <td>{license.name}</td>
                        <td>{license.email}</td>
                        <td>{license.licenseType}</td>
                        <td>{license.status}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="tracker-empty">No licenses yet</p>
              )}
            </>
          )}

          {trackerData.type === "registrations" && (
            <>
              {trackerData.items.length > 0 ? (
                <table className="tracker-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {trackerData.items.map((reg, idx) => (
                      <tr key={idx}>
                        <td>{reg.name}</td>
                        <td>{reg.email}</td>
                        <td>{reg.phone}</td>
                        <td>{reg.date}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="tracker-empty">No registrations yet</p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
/* ------------------------ CSS ------------------------ */
const CSS = `
:root {
  --bg: #0b0f16;
  --glass: rgba(255,255,255,.06);
  --muted: #9fb0c7;
  --gold: #ffd700;
  --red: #dc2626;
  --green: #16a34a;
  --dark-bg: rgba(0,0,0,0.7);
}

* {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

html, body {
  margin: 0;
  padding: 0;
  overflow: hidden;
  width: 100%;
  height: 100%;
}

.onboarding-road {
  height: 100vh;
  background: white;
  color: #fff;
  font-family: system-ui, -apple-system, sans-serif;
  overflow: hidden;
  width: 100%;
  display: flex;
  flex-direction: column;
}

.main-content-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow-x: auto;
  overflow-y: hidden;
  width: 100%;
  max-width: 100vw;
  -webkit-overflow-scrolling: touch;
}

.control-bar {
  position: sticky;
  top: 0;
  z-index: 50;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 20px;
  background: linear-gradient(180deg, rgba(0,0,0,.55), rgba(0,0,0,0));
  backdrop-filter: blur(6px);
  flex-wrap: wrap;
  height: 60px;
  min-height: 60px;
}

.brand {
  font-weight: 900;
  letter-spacing: .3px;
  color: var(--gold);
  font-size: 16px;
}

.reset-button {
  cursor: pointer;
  transition: all 0.3s ease;
  padding: 6px 10px;
  border-radius: 6px;
  user-select: none;
}

.reset-button:hover {
  background: rgba(255, 215, 0, 0.1);
  transform: scale(1.05);
  color: #ffeb3b;
}

.picker {
  display: flex;
  align-items: center;
  gap: 6px;
  background: var(--glass);
  padding: 6px 8px;
  border-radius: 8px;
}

.picker.wide {
  min-width: 300px;
  flex: 1;
  max-width: 400px;
}

.user-search-container {
  position: relative;
  flex: 1;
}

.user-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: #1a1f2e;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  max-height: 300px;
  overflow-y: auto;
  z-index: 1000;
  margin-top: 4px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
}

.user-dropdown-item {
  padding: 12px;
  cursor: pointer;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  transition: background 0.2s;
}

.user-dropdown-item:hover {
  background: rgba(255, 255, 255, 0.1);
}

.user-dropdown-item:last-child {
  border-bottom: none;
}

.user-dropdown-name {
  color: #fff;
  font-weight: 600;
  margin-bottom: 4px;
}

.user-dropdown-email {
  color: var(--muted);
  font-size: 12px;
}

.picker label {
  font-size: 13px;
  color: var(--muted);
  white-space: nowrap;
}

.picker input, .picker select {
  background: transparent;
  border: 0;
  outline: none;
  color: #fff;
  padding: 6px 8px;
  border-radius: 8px;
  min-width: 115px;
  width: 100%;
}

.status {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #cfe4ff;
  background: var(--glass);
  padding: 6px 10px;
  border-radius: 8px;
  font-size: 12px;
  white-space: nowrap;
}

.status .dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #666;
}

.status .dot.ok { background: var(--green); }
.status .dot.bad { background: var(--red); }
.status .dot.idle { background: #8a8a8a; }

.progress {
  position: relative;
  height: 8px;
  flex: 1;
  min-width: 150px;
  max-width: 250px;
  background: var(--glass);
  border-radius: 999px;
  overflow: hidden;
}

.progress .bar {
  position: absolute;
  inset: 0 auto 0 0;
  background: linear-gradient(90deg, #8a6d00, var(--gold));
  width: 0;
  transition: width .35s;
}

.progress span {
  position: absolute;
  top: 14px;
  right: 0;
  font-size: 12px;
  color: var(--muted);
  white-space: nowrap;
}

.road-scene-section {
  padding: 0;
  background: white;
  overflow-x: auto;
  overflow-y: hidden;
  flex-shrink: 0;
  -webkit-overflow-scrolling: touch;
}

.road-scene {
  position: relative;
  background-size: 111.11% 100%;
  background-repeat: no-repeat;
  background-position: left center;
  margin: 0 auto;
  width: 100%;
}

.section-header {
  position: absolute;
  top: 40px;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 80px;
  transform: translateX(-50%);
}

.section-title {
  font-weight: bold;
  font-size: 14px;
  text-align: center;
  color: #ffffff;
  text-shadow: 2px 2px 4px rgba(0,0,0,0.8);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  white-space: nowrap;
}

.section-badge {
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  perspective: 600px;
}

.section-badge img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  filter: drop-shadow(0 2px 4px rgba(0,0,0,0.5));
  transform-style: preserve-3d;
  animation: rotate3D 6s linear infinite;
}

.smd-badge {
  position: absolute;
  top: 220px;
  transform: translateX(-50%);
  z-index: 15;
}

.smd-pill {
  padding: 6px 12px;
  border-radius: 16px;
  font-weight: bold;
  font-size: 12px;
  box-shadow: 0 6px 12px rgba(0,0,0,0.4);
  text-align: center;
  white-space: nowrap;
  background: rgba(255, 215, 0, 0.95);
}

.smd-pill img{
  width: 90px;
  height: auto;
}

.traffic-lights-row {
  position: absolute;
  top: 120px;
  left: 0;
  right: 0;
  height: 140px;
}

.traffic-light-container {
  position: absolute;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  height: 130px;
  transform: translateX(-50%);
}

.traffic-light-image {
  width: auto;
  height: 90px;
  object-fit: contain;
  filter: drop-shadow(0 4px 8px rgba(0,0,0,0.5));
}

.module-label {
  font-size: 8px;
  font-weight: 700;
  color: #000;
  text-align: center;
  max-width: 80px;
  line-height: 1.1;
  margin-bottom: 4px;
  text-transform: uppercase;
  letter-spacing: 0.2px;
  min-height: 18px;
  display: flex;
  align-items: flex-end;
  justify-content: center;
}

.car {
  position: absolute;
  top: 260px;
  transition: left .55s cubic-bezier(.22,.61,.36,1);
  z-index: 10;
  transform: translateX(-50%);
}

.car-image {
  width: 140px;
  height: 30px;
  object-fit: contain;
  filter: drop-shadow(0 4px 8px rgba(0,0,0,0.5));
}

.video-section {
  background: white;
  backdrop-filter: blur(10px);
  flex-shrink: 0;
}

.video-dock {
  position: relative;
  margin: 0 auto;
  height: 80px !important;
}

.video-column {
  position: absolute;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  transform: translateX(-50%);
  top: 8px;
}

.video-tile {
  width: 36px;
  height: 30px;
  border: none;
  border-radius: 4px;
  background: var(--red);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1px;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0,0,0,0.2);
  font-size: 10px;
}

.video-tile:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.3);
}

.video-tile.locked {
  background: #6b7280;
  cursor: not-allowed;
  opacity: 0.6;
}

.video-tile.completed {
  background: var(--green);
}

.video-tile:disabled {
  cursor: not-allowed;
}

.video-tile .play-icon {
  color: white;
  font-size: 10px;
  font-weight: bold;
}

.video-label {
  font-size: 7px;
  color: white;
  font-weight: 500;
}

.video-start-title {
  font-size: 18px;
  font-weight: bold;
  margin-bottom: 4px;
  text-transform: uppercase;
  text-align: left;
  position: absolute;
  left: 0px;
  top: 2px;
  color: #000;
}

.video-start-image {
  width: 45px;
  height: auto;
  object-fit: contain;
  position: absolute;
  top: -2px;
  left: 8px;
}

.checkbox-section {
  background: white;
  padding: 0;
  flex-shrink: 0;
}

.checkbox-content {
  position: relative;
  display: flex;
  align-items: flex-start;
  color: black;
  height: 120px;
  margin: 0 auto;
  padding-top: 10px;
}

.agent-name {
  position: absolute;
  left: -10px;
  top: 10px;
  font-weight: bold;
  font-size: 16px;
  color: black;
  width: 150px;
  white-space: normal;
  word-wrap: break-word;
  line-height: 1.2;
}

.individual-checkbox-container {
  position: absolute;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  transform: translateX(-50%);
  top: 10px;
}

.checkbox-label {
  border-radius: 8px;
  padding: 2px 6px;
  font-size: 10px;
  font-weight: 700;
  color: #1a1a1a;
  text-align: center;
  line-height: 1.2;
  white-space: normal;
  margin-bottom: 2px;
  max-width:80px;
  min-height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.checkbox-column {
  display: flex;
  flex-direction: column;
  gap: 1px;
  align-items: center;
}

.aligned-checkbox {
  width: 20px;
  height: 20px;
  background: white;
  border: 2px solid #cbd5e1;
  border-radius: 4px;
  cursor: pointer;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.aligned-checkbox:checked {
  background: #22c55e;
  border-color: #16a34a;
}

.aligned-checkbox:checked::after {
  content: '✓';
  position: absolute;
  color: white;
  font-size: 14px;
  font-weight: 900;
  left: 50%;
  top: 48%;
  transform: translate(-50%, -50%);
}

.rolling-section-header {
  position: absolute;
  font-size: 10px;
  font-weight: 800;
  color: #d32f2f;
  text-align: center;
  letter-spacing: 1px;
  white-space: nowrap;
  transform: translateX(-50%);
}

/* Modal styles remain largely the same but with improved alignment */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,.85);
  backdrop-filter: blur(8px);
  z-index: 100;
  display: grid;
  place-items: center;
  padding: 20px;
  animation: fadeIn .3s;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.modal {
  background: #1a1f2e;
  border-radius: 16px;
  padding: 32px;
  max-width: 1400px;
  width: 95%;
  max-height: 90vh;
  box-shadow: 0 20px 60px rgba(0,0,0,.5);
  position: relative;
  animation: slideUp .3s;
  border: 2px solid var(--red);
  overflow-y: auto;
}

.modal h2 {
  margin: 0 0 12px 0;
  font-size: 24px;
  font-weight: 700;
  color: #fff;
}

.video-desc {
  color: var(--muted);
  margin-bottom: 20px;
  font-size: 14px;
  line-height: 1.5;
}

.video-container {
  width: 100%;
  margin: 0 auto 20px;
  border-radius: 12px;
  overflow: hidden;
  background: #000;
  aspect-ratio: 16 / 9;
  max-height: 70vh;
}

.onboarding-modal-video {
  width: 100%;
  height: 100%;
  border: none;
  border-radius: 12px;
}

.watch-indicator {
  margin-bottom: 20px;
  text-align: center;
}

.watch-bar {
  width: 100%;
  height: 6px;
  background: rgba(255,255,255,.1);
  border-radius: 3px;
  overflow: hidden;
  margin-bottom: 10px;
}

.watch-fill {
  height: 100%;
  background: linear-gradient(90deg, var(--green), #22c55e);
  transition: width .3s ease;
}

.watch-indicator span {
  color: var(--muted);
  font-size: 13px;
}

.modal-btn {
  width: 100%;
  padding: 14px 24px;
  background: var(--red);
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all .2s;
}

.modal-btn:hover {
  background: #b91c1c;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(220, 38, 38, 0.3);
}

.modal-btn:disabled {
  background: #6b7280;
  cursor: not-allowed;
  transform: none;
}

@keyframes slideUp {
  from { transform: translateY(20px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}

.modal-close {
  position: absolute;
  top: 16px;
  right: 16px;
  background: var(--glass);
  border: 0;
  color: #fff;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  cursor: pointer;
  font-size: 18px;
  transition: all .2s;
  border: 1px solid var(--red);
}

.modal-close:hover {
  background: var(--red);
  transform: rotate(90deg);
}

/* Improved scrollbar hiding */
::-webkit-scrollbar {
  display: none;
}

* {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

/* Responsive improvements */
@media (max-width: 1440px) {
  .picker.wide {
    min-width: 250px;
  }
  
  .progress {
    min-width: 120px;
  }
  
  .section-title {
    font-size: 12px;
  }
  
  .traffic-light-image {
    height: 80px;
  }
}

@media (max-width: 1024px) {
  .control-bar {
    padding: 8px 16px;
    gap: 8px;
  }
  
  .picker.wide {
    min-width: 200px;
  }
  
  .brand {
    font-size: 14px;
  }
}

/* Ensure proper alignment of all sections */
.road-scene-section,
.video-section,
.checkbox-section {
  display: flex;
  justify-content: center;
  width: 100%;
}

.road-scene,
.video-dock,
.checkbox-content {
  position: relative;
}

/* Fix alignment for video start */
.video-start {
  position: absolute;
  display: flex;
  flex-direction: column;
  align-items: center;
  transform: translateX(-50%);
}

@keyframes rotate3D {
  0% { transform: rotateY(0deg) rotateX(0deg); }
  50% { transform: rotateY(180deg) rotateX(0deg); }
  100% { transform: rotateY(360deg) rotateX(0deg); }
}

body, html, .onboarding-road, .main-content-container {
  overflow-x: hidden !important;
}

.value-input-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  margin-bottom: 4px;
}

.value-input {
  width: 60px;
  height: 24px;
  text-align: center;
  border: 1px solid #cbd5e1;
  border-radius: 4px;
  background: white;
  color: #1a1a1a;
  font-size: 10px;
  font-weight: 600;
  cursor: pointer;
  padding: 2px 4px;
}

.value-input.completed {
  background: #dcfce7;
  border-color: #16a34a;
  color: #166534;
}

.value-input.in-progress {
  background: #fef3c7;
  border-color: #d97706;
  color: #92400e;
}

.progress-indicator {
  width: 60px;
  height: 4px;
  background: #e5e7eb;
  border-radius: 2px;
  overflow: hidden;
}

.progress-bar {
  height: 100%;
  background: linear-gradient(90deg, #3b82f6, #10b981);
  transition: width 0.3s ease;
}

.target-text {
  font-size: 8px;
  color: #6b7280;
  font-weight: 600;
}

.completion-checkbox {
  width: 16px;
  height: 16px;
  margin-top: 2px;
}

.loading-indicator {
  font-size: 10px;
  color: #6b7280;
  font-style: italic;
}

/* Ensure proper alignment */
.individual-checkbox-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

/* Prospects Modal Styles */
.modal-overlay-glass {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  animation: fadeInGlass 0.15s ease;
  padding: 20px;
}

@keyframes fadeInGlass {
  from { opacity: 0; }
  to { opacity: 1; }
}

.prospects-modal-glass-v2 {
  width: 95vw;
  max-width: 1600px;
  height: 85vh;
  max-height: 800px;
  background: linear-gradient(135deg, rgba(26, 26, 31, 0.98), rgba(37, 40, 48, 0.95));
  border: 1px solid rgba(255, 215, 0, 0.3);
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5), 0 0 40px rgba(255, 215, 0, 0.1);
  backdrop-filter: blur(20px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: slideUpGlass 0.2s ease;
}

@keyframes slideUpGlass {
  from {
    opacity: 0;
    transform: translateY(20px) scale(0.98);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.prospects-modal-header-glass {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid rgba(255, 215, 0, 0.2);
  background: linear-gradient(to bottom, rgba(255, 215, 0, 0.08), transparent);
}

.prospects-modal-header-glass h2 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #ffd700;
  text-shadow: 0 0 20px rgba(255, 215, 0, 0.3);
}

.prospects-modal-close-glass {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: #fff;
  font-size: 20px;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.prospects-modal-close-glass:hover {
  background: rgba(255, 59, 59, 0.8);
  border-color: rgba(255, 59, 59, 1);
  transform: scale(1.05);
}

.prospects-modal-search {
  padding: 12px 20px;
  border-bottom: 1px solid rgba(255, 215, 0, 0.1);
}

.prospects-search-input-glass {
  width: 100%;
  padding: 10px 14px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 215, 0, 0.3);
  border-radius: 8px;
  color: #fff;
  font-size: 14px;
  outline: none;
  transition: all 0.2s;
}

.prospects-search-input-glass:focus {
  background: rgba(255, 255, 255, 0.08);
  border-color: rgba(255, 215, 0, 0.6);
  box-shadow: 0 0 0 3px rgba(255, 215, 0, 0.1);
}

.prospects-search-input-glass::placeholder {
  color: rgba(255, 255, 255, 0.4);
}

.prospects-table-wrapper-resizable {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.prospects-table-scroll {
  flex: 1;
  overflow: auto;
  min-height: 0;
}

.prospects-table-resizable {
  width: max-content;
  min-width: 100%;
  border-collapse: collapse;
  border-spacing: 0;
}

.prospects-table-resizable thead {
  position: sticky;
  top: 0;
  z-index: 10;
  background: linear-gradient(to bottom, #252830, #1f2128);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

.prospects-table-resizable th {
  padding: 14px 12px;
  text-align: left;
  font-weight: 700;
  font-size: 12px;
  text-transform: uppercase;
  color: #ffd700;
  border-right: 1px solid rgba(255, 255, 255, 0.08);
  border-bottom: 1px solid rgba(255, 215, 0, 0.3);
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
  position: relative;
}

.prospects-table-resizable th:hover {
  background: rgba(255, 255, 255, 0.05);
}

.prospects-table-resizable td {
  padding: 12px 10px;
  border-right: 1px solid rgba(255, 255, 255, 0.05);
  border-bottom: 1px solid rgba(255, 255, 255, 0.03);
  color: #fff;
  font-size: 13px;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
}

.resize-handle {
  position: absolute;
  right: -3px;
  top: 0;
  bottom: 0;
  width: 6px;
  cursor: col-resize;
  background: transparent;
  z-index: 200;
  transition: background 0.2s ease;
}

.resize-handle:hover {
  background: linear-gradient(to right, transparent 30%, rgba(255, 215, 0, 0.3), transparent 70%);
}

.prospects-table-resizable tbody tr {
  transition: all 0.2s ease;
}

.prospects-table-resizable tbody tr:hover {
  background: rgba(255, 255, 255, 0.03) !important;
  box-shadow: inset 0 0 0 1px rgba(255, 215, 0, 0.2);
}

.prospect-marker-inline {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  display: inline-block;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  border: 2px solid rgba(255, 255, 255, 0.2);
}

.outcome-badge-inline {
  padding: 4px 10px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
  display: inline-block;
  text-transform: capitalize;
}

.outcome-badge-inline.client {
  background: rgba(0, 230, 118, 0.15);
  color: #00E676;
  border: 1px solid rgba(0, 230, 118, 0.3);
}

.outcome-badge-inline.recruit {
  background: rgba(41, 121, 255, 0.15);
  color: #2979FF;
  border: 1px solid rgba(41, 121, 255, 0.3);
}

.outcome-badge-inline.both {
  background: rgba(255, 179, 0, 0.15);
  color: #FFB300;
  border: 1px solid rgba(255, 179, 0, 0.3);
}

.notes-cell-inline {
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
}

.note-input-inline {
  flex: 1;
  min-width: 0;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 6px;
  padding: 6px 10px;
  color: #fff;
  font-size: 12px;
  outline: none;
  transition: all 0.2s ease;
}

.note-input-inline:focus {
  background: rgba(255, 255, 255, 0.08);
  border-color: rgba(255, 215, 0, 0.5);
  box-shadow: 0 0 0 2px rgba(255, 215, 0, 0.1);
}

.note-input-inline.showing-existing {
  background: rgba(255, 215, 0, 0.08);
  border-left: 3px solid rgba(255, 215, 0, 0.4);
  padding-left: 8px;
  font-style: italic;
  opacity: 0.95;
}

.notes-openall-btn {
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  padding: 6px 10px;
  cursor: pointer;
  font-size: 14px;
  line-height: 1;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.notes-openall-btn:hover {
  background: rgba(255, 215, 0, 0.15);
  border-color: rgba(255, 215, 0, 0.4);
}

.note-meta-inline {
  font-size: 10px;
  opacity: 0.7;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-top: 2px;
  padding-left: 10px;
}

.actions-inline-btns {
  display: flex;
  gap: 6px;
  align-items: center;
}

.action-btn-inline {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  font-size: 14px;
  text-decoration: none;
  transition: all 0.2s ease;
}

.action-btn-inline:hover {
  background: rgba(255, 215, 0, 0.15);
  border-color: rgba(255, 215, 0, 0.4);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(255, 215, 0, 0.2);
}

.prospects-empty-glass-v2 {
  padding: 40px 20px;
  text-align: center;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.5);
  font-style: italic;
}

.prospects-loading-glass {
  padding: 40px 20px;
  text-align: center;
  color: rgba(255, 255, 255, 0.5);
  font-size: 14px;
}

.prospects-modal-footer-glass {
  padding: 12px 20px;
  border-top: 1px solid rgba(255, 215, 0, 0.1);
  text-align: center;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.6);
  background: linear-gradient(to top, rgba(255, 215, 0, 0.05), transparent);
}

/* Notes Modal Styles */
.modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999999;
  backdrop-filter: blur(4px);
}

.modal-card {
  background: #1f2128;
  color: #fff;
  border: 1px solid #3a3f4a;
  border-radius: 12px;
  box-shadow: 0 14px 40px rgba(0, 0, 0, 0.6);
  padding: 20px;
  max-height: 80vh;
  overflow: auto;
  width: min(800px, 90vw);
}

.modal-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}

.modal-head h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
}

.btn {
  padding: 8px 16px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;
  background: transparent;
  color: #fff;
}

.btn.ghost {
  background: transparent;
}

.btn.ghost:hover {
  background: rgba(255, 255, 255, 0.1);
}

.btn.primary {
  background: rgba(255, 215, 0, 0.2);
  border-color: rgba(255, 215, 0, 0.4);
  color: #ffd700;
  font-weight: 600;
}

.btn.primary:hover {
  background: rgba(255, 215, 0, 0.3);
}

.notes-list-modal {
  max-height: 40vh;
  overflow: auto;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 10px;
  padding: 8px;
  margin-bottom: 12px;
}

.empty-notes {
  opacity: 0.7;
  text-align: center;
  padding: 16px 8px;
  font-style: italic;
}

.note-row-modal {
  display: grid;
  grid-template-columns: 1fr auto auto;
  gap: 8px;
  align-items: center;
  padding: 10px;
  border-radius: 8px;
  transition: background 0.2s ease;
}

.note-row-modal:hover {
  background: rgba(255, 255, 255, 0.05);
}

.note-row-modal:not(:last-child) {
  border-bottom: 1px dashed rgba(255, 255, 255, 0.08);
}

.note-text-modal {
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 13px;
  line-height: 1.5;
}

.note-meta-modal {
  opacity: 0.7;
  font-size: 11px;
  white-space: nowrap;
}

.note-delete-modal {
  background: transparent;
  border: 1px dashed rgba(255, 100, 100, 0.4);
  color: rgba(255, 100, 100, 0.9);
  border-radius: 6px;
  padding: 4px 8px;
  cursor: pointer;
  font-size: 11px;
  font-weight: 600;
  transition: all 0.2s ease;
}

.note-delete-modal:hover {
  background: rgba(255, 100, 100, 0.1);
  border-color: rgba(255, 100, 100, 0.6);
}

.note-delete-modal:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.notes-modal-add {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.notes-modal-add .input {
  width: 100%;
  padding: 10px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 6px;
  color: #fff;
  font-size: 13px;
  resize: vertical;
  outline: none;
  transition: all 0.2s ease;
}

.notes-modal-add .input:focus {
  background: rgba(255, 255, 255, 0.08);
  border-color: rgba(255, 215, 0, 0.5);
  box-shadow: 0 0 0 2px rgba(255, 215, 0, 0.12);
}

/* Scrollbar Styles */
.prospects-table-scroll::-webkit-scrollbar {
  width: 10px;
  height: 10px;
}

.prospects-table-scroll::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.03);
  border-radius: 8px;
}

.prospects-table-scroll::-webkit-scrollbar-thumb {
  background: rgba(255, 215, 0, 0.3);
  border-radius: 8px;
  border: 2px solid rgba(26, 26, 31, 1);
}

.prospects-table-scroll::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 215, 0, 0.5);
}

.notes-list-modal::-webkit-scrollbar {
  width: 6px;
}

.notes-list-modal::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 3px;
}

.notes-list-modal::-webkit-scrollbar-thumb {
  background: rgba(255, 215, 0, 0.3);
  border-radius: 3px;
}

.notes-list-modal::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 215, 0, 0.5);
}
`;

